// src/contexts/AuthContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';

const AuthContext = createContext(undefined);

// API URL
const API_URL = process.env.REACT_APP_API_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authError, setAuthError] = useState(null);

  // Check if token exists in localStorage on init
  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      const storedToken = localStorage.getItem('authToken');
      
      if (storedToken) {
        setToken(storedToken);
        await checkAuthorization(storedToken);
      } else {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Handle Google credential response - updated to work with GoogleLogin component
  async function signInWithGoogle(credentialResponse) {
    setLoading(true);
    setAuthError(null);
    
    console.log("Handling Google sign-in response:", credentialResponse);
    
    // Extract ID token from credential response
    const idToken = credentialResponse.credential;
    
    if (!idToken) {
      console.error("No credential token received from Google");
      setAuthError('Failed to get authentication token from Google');
      setLoading(false);
      return;
    }
    
    console.log("Received ID token (first 20 chars):", idToken.substring(0, 20) + "...");
    
    // Store token in localStorage
    localStorage.setItem('authToken', idToken);
    setToken(idToken);
    
    // Check if user is authorized
    await checkAuthorization(idToken);
  }

  // Check if user is authorized
  async function checkAuthorization(token) {
    try {
      console.log("Checking authorization with token (first 20 chars):", token.substring(0, 20) + "...");
      
      console.log("Calling API: " + API_URL + "/auth/check");
      const response = await fetch(API_URL + "/auth/check", {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log("Auth check response status:", response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log("User data received:", data);
        setUser(data.user);
        setIsAuthorized(true);
        setAuthError(null);
      } else {
        console.log("Auth check failed with status:", response.status);
        let errorMessage = 'Authorization failed';
        
        try {
          const errorData = await response.json();
          console.log("Error data:", errorData);
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          console.error("Failed to parse error response:", e);
        }
        
        setAuthError(errorMessage);
        setIsAuthorized(false);
        setUser(null);
        localStorage.removeItem('authToken');
        setToken(null);
      }
    } catch (error) {
      console.error('Error checking authorization:', error);
      setAuthError('Failed to verify authorization');
      setIsAuthorized(false);
      setUser(null);
      localStorage.removeItem('authToken');
      setToken(null);
    } finally {
      setLoading(false);
    }
  }

  // Sign out
  function signOut() {
    localStorage.removeItem('authToken');
    setToken(null);
    setUser(null);
    setIsAuthorized(false);
  }

  const value = {
    user,
    token,
    loading,
    isAuthorized,
    authError,
    signInWithGoogle,
    signOut
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};